import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import { QRCode } from 'react-qr-code';
import { getQRCode } from '../../api/registration';
import Cookies from 'js-cookie';
import { resolvePageContext, resolveRegisterPath } from '../../utils/fanMemberUtils';
import { getFanMemberBrandTheme } from '../../config/fanMembersConfig';

const QRCodePage = ({ fixedActivityType, fixedId, fixedFanSlug }) => {
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const pageContext = resolvePageContext({ fixedFanSlug, fixedActivityType, fixedId, params });
  const { activityId: id, activityType, isFanMemberRoute, navigateTo, fanMember } = pageContext;
  const brand = getFanMemberBrandTheme(fanMember);
  const registerPath = resolveRegisterPath({ isFanMemberRoute, navigateTo, activityType, activityId: id });
  const registrationId = Cookies.get('registrationId');

  useEffect(() => {
    const fetchQRCode = async () => {
      try {
        if (!registrationId) {
          throw new Error('No registration ID found');
        }

        const response = await getQRCode(registrationId);
        if (response.success && response.data.isVerified) {
          setQrCode(response.data.qrCode);
          setTimeout(() => setIsVisible(true), 200);
        } else {
          throw new Error('Invalid QR code');
        }
      } catch (err) {
        setError(err.message);
        setTimeout(() => {
          if (isFanMemberRoute) {
            navigate(navigateTo());
          } else {
            navigate(registerPath);
          }
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    if (!registrationId) {
      setError('No registration ID found, please register or login again');
      setTimeout(() => {
        if (isFanMemberRoute) {
          navigate(navigateTo());
        } else {
          navigate(`/${activityType}/${id}/register`);
        }
      }, 3000);
      return;
    }

    fetchQRCode();
  }, [registrationId, activityType, id, navigate]);

  // Download QR Code functionality
  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = 300;
    canvas.height = 300;
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);
      
      const link = document.createElement('a');
      link.download = 'verification-qr-code.png';
      link.href = canvas.toDataURL();
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isFanMemberRoute ? brand.pageBg : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'}`}>
        <div className={`text-center bg-white rounded-xl shadow-lg p-8 max-w-sm w-full mx-4 border ${isFanMemberRoute ? brand.cardBorder : ''}`}>
          <div className="mb-6">
            <div className={`w-16 h-16 ${isFanMemberRoute ? brand.iconBg : 'bg-gradient-to-br from-blue-500 to-purple-600'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <svg className="w-8 h-8 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Generating QR Code</h2>
            <p className="text-gray-600 text-sm">Please wait while we create your verification code...</p>
          </div>
          <BeatLoader color={isFanMemberRoute ? brand.loader : '#7c3aed'} loading={loading} size={10} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isFanMemberRoute ? brand.pageBg : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'}`}>
        <div className={`max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border ${isFanMemberRoute ? brand.cardBorder : ''}`}>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className={`text-sm ${isFanMemberRoute ? brand.accentText : 'text-blue-600'}`}>Redirecting you back automatically...</p>
        </div>
      </div>
    );
  }

  if (!qrCode) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isFanMemberRoute ? brand.pageBg : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'}`}>
        <div className={`max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border ${isFanMemberRoute ? brand.cardBorder : ''}`}>
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">QR Code Not Found</h2>
          <p className="text-gray-600">Please complete your registration first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isFanMemberRoute ? brand.pageBg : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'}`}>
      <div className={`min-h-screen flex items-center justify-center p-4 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className={`max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border ${isFanMemberRoute ? brand.cardBorder : 'border-blue-200'}`}>
          {/* Header */}
          <div className="mb-8">
            <div className={`w-16 h-16 ${isFanMemberRoute ? brand.iconBg : 'bg-gradient-to-br from-blue-500 to-purple-600'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Verification QR Code
            </h1>
            <p className="text-gray-600 text-sm">Your registration is confirmed and ready for verification</p>
          </div>

          {/* QR Code Container */}
          <div className="bg-gray-50 p-6 rounded-lg mb-8 border border-gray-200">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <QRCode
                id="qr-code-svg"
                value={qrCode}
                size={window.innerWidth < 640 ? 200 : 256}
                level="H"
                className="mx-auto"
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 mb-6">
            <button
              onClick={downloadQRCode}
              className={`w-full text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 ${isFanMemberRoute ? brand.button : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              Download QR Code
            </button>
            
            <button
              onClick={() => navigate(registerPath)}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Registration
            </button>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-blue-800 font-medium text-sm mb-1">Instructions</p>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Present this QR code at the entrance for verification. Keep it accessible on your device or print a copy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodePage;